/**
 * Color utility functions for validation, format conversion, and manipulation
 */

/**
 * Validate hex color format
 *
 * @param {string} color - The color to validate
 * @returns {boolean} True if valid hex color
 */
export function isValidHexColor(color) {
  if (!color || typeof color !== 'string') {
    return false;
  }

  // Allow empty string (no color override)
  if (color === '') {
    return true;
  }

  // Check hex format (#RGB or #RRGGBB)
  const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexPattern.test(color);
}

/**
 * Normalize hex color to 6-digit format
 * #RGB -> #RRGGBB
 *
 * @param {string} color - The hex color to normalize
 * @returns {string} Normalized hex color
 */
export function normalizeHexColor(color) {
  if (!color || !isValidHexColor(color)) {
    return color;
  }

  // Already 6 digits
  if (color.length === 7) {
    return color.toUpperCase();
  }

  // Expand 3 digits to 6
  if (color.length === 4) {
    const r = color[1];
    const g = color[2];
    const b = color[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  return color;
}

/**
 * Convert hex color to RGB
 *
 * @param {string} hex - The hex color
 * @returns {object|null} RGB object {r, g, b} or null if invalid
 */
export function hexToRgb(hex) {
  const normalized = normalizeHexColor(hex);
  if (!normalized || normalized.length !== 7) {
    return null;
  }

  const result = /^#([A-Fa-f0-9]{2})([A-Fa-f0-9]{2})([A-Fa-f0-9]{2})$/.exec(normalized);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Convert RGB to hex color
 *
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} Hex color
 */
export function rgbToHex(r, g, b) {
  const toHex = (n) => {
    const hex = Math.max(0, Math.min(255, Math.round(n))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Lighten a hex color by a percentage
 *
 * @param {string} color - The hex color
 * @param {number} percent - Percent to lighten (0-1)
 * @returns {string} Lightened hex color
 */
export function lightenColor(color, percent) {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  const amount = Math.round(255 * percent);
  const r = Math.min(255, rgb.r + amount);
  const g = Math.min(255, rgb.g + amount);
  const b = Math.min(255, rgb.b + amount);

  return rgbToHex(r, g, b);
}

/**
 * Darken a hex color by a percentage
 *
 * @param {string} color - The hex color
 * @param {number} percent - Percent to darken (0-1)
 * @returns {string} Darkened hex color
 */
export function darkenColor(color, percent) {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  const amount = Math.round(255 * percent);
  const r = Math.max(0, rgb.r - amount);
  const g = Math.max(0, rgb.g - amount);
  const b = Math.max(0, rgb.b - amount);

  return rgbToHex(r, g, b);
}

/**
 * Get color luminance (brightness)
 *
 * @param {string} color - The hex color
 * @returns {number} Luminance value (0-1)
 */
export function getColorLuminance(color) {
  const rgb = hexToRgb(color);
  if (!rgb) return 0;

  // Use relative luminance formula
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Determine if color is light or dark
 *
 * @param {string} color - The hex color
 * @returns {string} 'light' or 'dark'
 */
export function getColorBrightness(color) {
  const luminance = getColorLuminance(color);
  return luminance > 0.5 ? 'light' : 'dark';
}

/**
 * Get contrasting text color (black or white)
 *
 * @param {string} backgroundColor - The background hex color
 * @returns {string} '#000000' or '#FFFFFF'
 */
export function getContrastTextColor(backgroundColor) {
  const brightness = getColorBrightness(backgroundColor);
  return brightness === 'light' ? '#000000' : '#FFFFFF';
}

/**
 * Sanitize color value
 * Returns empty string if invalid
 *
 * @param {string} color - The color to sanitize
 * @returns {string} Sanitized color or empty string
 */
export function sanitizeColor(color) {
  if (!color || typeof color !== 'string') {
    return '';
  }

  const trimmed = color.trim();

  // Allow empty string
  if (trimmed === '') {
    return '';
  }

  // Validate and normalize hex colors
  if (isValidHexColor(trimmed)) {
    return normalizeHexColor(trimmed);
  }

  // Invalid color
  console.warn('Invalid color format:', color);
  return '';
}

/**
 * Generate color palette from base color
 *
 * @param {string} baseColor - The base hex color
 * @returns {object} Color palette with lighter/darker variants
 */
export function generateColorPalette(baseColor) {
  if (!isValidHexColor(baseColor)) {
    return {};
  }

  return {
    base: baseColor,
    lighter: lightenColor(baseColor, 0.3),
    light: lightenColor(baseColor, 0.15),
    dark: darkenColor(baseColor, 0.15),
    darker: darkenColor(baseColor, 0.3)
  };
}
